import React from 'react';
import { EntityId } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { destroyReport, updateReport } from "src/actions/community/reportsActions";
import { ReportInterface, selectAllReports } from "src/store/community/reportsSlice";
import { selectUserEntities, setBanId } from "src/store/community/usersSlice";
import { selectPostEntities } from "src/store/community/postsSlice";
import { formattedDate } from "src/helpers/DateHelpers";

import {message, Space, Tag, Tooltip} from "antd";
import type { ColumnsType } from 'antd/es/table';
import {LockOutlined, StopOutlined, UnlockOutlined} from '@ant-design/icons';
import { Button, ButtonDestroy } from "src/componets/Buttons";
import UserMeta from "../../Users/_partials/_Meta";
import PostMeta from "../../Posts/_partials/_Meta";

import Table from "src/componets/Table";
import Block from "../../Users/Block";
import OptionalLink from "src/componets/OptionalLink";
import { selectCommentEntities } from "src/store/community/commentsSlice";

interface propsInterface {
  limit: number;
}

const REASON_TAGS: { [key: string]: string } = {
  1: 'red',
  2: 'blue',
  3: 'geekblue',
  4: 'orange'
};

const _List = ({ limit }: propsInterface) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const reports = useAppSelector(selectAllReports);
  const users = useAppSelector(selectUserEntities);
  const posts  = useAppSelector(selectPostEntities);
  const comments = useAppSelector(selectCommentEntities);
  const count = useAppSelector(state => state.community.reports.count);
  const error = useAppSelector(state => state.community.reports.error);
  const status = useAppSelector(state => state.community.reports.status);

  function onClickDestroy(id: EntityId)  {
    handleDestroy(id).catch(console.error);
  }

  async function handleDestroy (id: any) {
    const resultAction = await dispatch(destroyReport(id));

    if (destroyReport.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
      setTimeout(() => navigate(0), 1000);
    }

    if (destroyReport.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  function onToggleClose(report: ReportInterface) {
    dispatch(updateReport({ id: report.id, data: { closed: !report.closed } }));
  }

  function onClickBan(id: EntityId) {
    if (id) {
      dispatch(setBanId(id));
    }
  }

  const CloseReportBtn = ({ report }: { report: ReportInterface }) => {
    if (report.closed) {
      return <Button key='button-close' onClick={() => onToggleClose(report)} title='Open' icon={UnlockOutlined} />
    } else {
      return <Button key='button-close' onClick={() => onToggleClose(report)} title='Close' icon={LockOutlined} />
    }
  }

  const renderTarget = (_, { target_id, target_type }) => {
    switch (target_type) {
      case 'Post':
        return <PostMeta post={posts[target_id]} size='small' />;
      case 'User':
        return <UserMeta user={users[target_id]} size='small' shape='circle' />;
      case 'Comment':
        return(
          <OptionalLink link={comments[target_id]?.url}>
            <Tooltip placement="topLeft" title={comments[target_id]?.body}>
              {comments[target_id]?.body}
            </Tooltip>
          </OptionalLink>
        );
      default:
        return <span>Unknown</span>;
    }
  }

  const columns: ColumnsType<ReportInterface> = [
    {
      key: 'closed',
      title: 'Status',
      dataIndex: 'closed',
      render: (_, { closed }) => {
        if (closed) {
          return <Tag style={{ fontSize: 10 }} color='#b7c0cd'>CLOSED</Tag>;
        } else {
          return <Tag style={{ fontSize: 10 }} color='#fd9a18'>OPEN</Tag>;
        }
      },
      width: 90
    },

    {
      key: 'target_id',
      title: 'Target',
      dataIndex: 'target_id',
      render: renderTarget,
      ellipsis: true
    },

    {
      key: 'user_id',
      title: 'Reported by',
      dataIndex: 'user_id',
      render: (_, { active_user_id }) => {
        return <UserMeta user={users[active_user_id]} size='small' shape='circle' />;
      },
      ellipsis: true
    },

    Table.EXPAND_COLUMN,

    {
      key: 'reason_id',
      title: 'Reason',
      dataIndex: 'reason_id',
      render: (_, { reason_id, reason }) => {
        const colorTag = REASON_TAGS[reason_id];
        return <Tag color={colorTag}>{reason.toUpperCase()}</Tag>;
      },
    },

    {
      key: 'created_at',
      title: 'Created at',
      render: (_, { created_at }) => {
        return <small>{formattedDate(created_at).dateTime}</small>;
      },
      width: 200
    },

    {
      key: 'action',
      title: 'Action',
      render: (_, record) => {
        return (
          <Space size="middle">
            <CloseReportBtn report={record} />
            <Button key='button-ban' onClick={() => onClickBan(record.user_id)} title='Block' icon={StopOutlined} />
            <ButtonDestroy key='button-destroy' onClick={() => onClickDestroy(record.id)} />
          </Space>
        );
      },
      width: 250
    },
  ];

  if (status === 'failed') {
    return <div>Error: {error}</div>
  }

  return (
    <>
      <Table
        rowKey='id'
        loading={status === 'loading'}
        columns={columns}
        dataSource={reports}
        pagination={{
          className: 'v-mt-15',
          align: 'right',
          total: count,
          size: limit
        }}
        expandable={{
          expandedRowRender: record => <span><strong>Reason Comment:</strong> {record.reason_text}</span>,
          rowExpandable: record => !!record.reason_text,
        }}
      />

      <Block />
    </>
  );
}

export default _List;
